import React from 'react';

export type CashFlowStatement = {
  purchasePrice: number;
  propertyTypeLabel: string;
  units?: number;
  monthlyRent: number;
  annualRent: number;
  expenseLines?: Array<{ key: string; label: string; monthly: number; annual: number }>;
  noiMonthly: number;
  noiAnnual: number;
  mortgage?: {
    downPaymentPct: number;
    interestRate: number;
    termYears: number;
    monthlyPayment: number;
    loanAmount: number;
    loanType?: string;
  };
  secondaryMortgage?: {
    label?: string;
    interestRate: number;
    termYears: number;
    monthlyPayment: number;
    loanAmount: number;
    loanType?: string;
  } | null;
  totalDebtServiceMonthly?: number;
  totalDebtServiceAnnual?: number;
  cashFlowMonthly: number;
  cashFlowAnnual: number;
  cashOnCashReturn?: number | null;
  ruleOf72Years?: number | null;
};

const money = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString()}` : 'N/A';

const moneyPerMonthYear = (monthly: number | null | undefined, annual: number | null | undefined) => {
  if (monthly == null && annual == null) return 'N/A';
  const m = monthly != null ? money(monthly) : money((annual || 0) / 12);
  const y = annual != null ? money(annual) : money((monthly || 0) * 12);
  return `${m}/month · ${y}/year`;
};

const formatRate = (rate: number | null | undefined) => {
  if (rate == null || !Number.isFinite(Number(rate))) return '—';
  const pct = Math.round(Number(rate) * 1000) / 1000;
  return `${pct}%`;
};

const formatLoanType = (loanType: string | null | undefined) => {
  const value = String(loanType || '').toUpperCase();
  if (value === 'INTEREST_ONLY') return 'interest-only';
  return 'fully amortizing';
};

const pctOfRent = (amount: number | null | undefined, monthlyRent: number | null | undefined) => {
  const rent = Number(monthlyRent);
  const expense = Number(amount);
  if (!Number.isFinite(rent) || rent <= 0 || !Number.isFinite(expense)) return '—';
  return `${(Math.round((expense / rent) * 1000) / 10).toFixed(1)}%`;
};

const CashFlowProformaTable: React.FC<{ cf: CashFlowStatement }> = ({ cf }) => {
  const negative = (cf.cashFlowAnnual ?? 0) < 0;
  return (
    <>
      <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 12 }}>
        Purchase price {money(cf.purchasePrice)} · {cf.propertyTypeLabel}
        {cf.units != null && cf.units > 1 ? ` · ${cf.units} units` : ''}
      </p>
      <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
        Rent {moneyPerMonthYear(cf.monthlyRent, cf.annualRent)}
      </p>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Minus operating expenses</p>
      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            border: '1px solid #e5e7eb',
          }}
        >
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', background: '#f9fafb' }}>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Expense</th>
              <th style={{ padding: '8px 10px', fontWeight: 600, width: 72 }}>% of rent</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Monthly / annual</th>
            </tr>
          </thead>
          <tbody>
            {(cf.expenseLines || []).map((line) => (
              <tr key={line.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px 10px' }}>{line.label}</td>
                <td style={{ padding: '8px 10px', color: '#6b7280' }}>{pctOfRent(line.monthly, cf.monthlyRent)}</td>
                <td style={{ padding: '8px 10px' }}>{moneyPerMonthYear(line.monthly, line.annual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
        Net operating income (NOI) {moneyPerMonthYear(cf.noiMonthly, cf.noiAnnual)}
      </p>
      <p style={{ fontSize: 14, marginBottom: 8 }}>
        Minus 1st mortgage ({cf.mortgage?.downPaymentPct}% down @ {formatRate(cf.mortgage?.interestRate)} /{' '}
        {cf.mortgage?.termYears} yrs · {formatLoanType(cf.mortgage?.loanType)}):{' '}
        {money(cf.mortgage?.monthlyPayment)}/month ({money(cf.mortgage?.loanAmount)} loan)
      </p>
      {cf.secondaryMortgage && cf.secondaryMortgage.loanAmount > 0 && (
        <p style={{ fontSize: 14, marginBottom: 8 }}>
          Minus {cf.secondaryMortgage.label || '2nd mortgage'} (@ {formatRate(cf.secondaryMortgage.interestRate)} /{' '}
          {cf.secondaryMortgage.termYears} yrs · {formatLoanType(cf.secondaryMortgage.loanType)}):{' '}
          {money(cf.secondaryMortgage.monthlyPayment)}/month ({money(cf.secondaryMortgage.loanAmount)} loan)
        </p>
      )}
      {cf.secondaryMortgage &&
        cf.secondaryMortgage.loanAmount > 0 &&
        cf.totalDebtServiceMonthly != null && (
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
            Total debt service {money(cf.totalDebtServiceMonthly)}/month
          </p>
        )}
      <p
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: negative ? '#b91c1c' : '#15803d',
          marginBottom: 6,
        }}
      >
        Cash flow {moneyPerMonthYear(cf.cashFlowMonthly, cf.cashFlowAnnual)}
      </p>
      <p style={{ fontSize: 13, color: '#374151' }}>
        Cash-on-cash return: {cf.cashOnCashReturn != null ? `${cf.cashOnCashReturn}%` : 'N/A'}
        {cf.ruleOf72Years != null ? ` · Rule of 72: ~${cf.ruleOf72Years} years to double` : ''}
      </p>
    </>
  );
};

export default CashFlowProformaTable;
